import PillBadge from 'components/atomic/organisms/pill-badge';
import IconWrapper from 'components/atomic/atoms/icon-wrapper';
import { SensorDoorIcon, ViewInArIcon, TrendingDownIcon, CalendarIcon, StarIcon } from 'components/atomic/atoms/icons';
import { semanticColors } from 'design-system';
import type { ComponentType, ReactNode } from 'react';

/**
 * Badge variant identifiers
 */
export type BadgeVariant = 'new' | 'open-house' | 'virtual-tour' | 'price-reduced' | 'coming-soon' | 'exp-early-access' | 'smart-tag';

/**
 * Configuration for a badge variant
 */
interface BadgeVariantConfig {
  backgroundColor: string;
  textColor: string;
  borderColor?: string;
  icon?: ComponentType;
  ariaLabelSuffix?: string;
}

/**
 * Badge variant configurations
 * 
 * Following the tenant config pattern - all visual/behavioral differences
 * are captured in configuration rather than separate components.
 */
const BADGE_VARIANT_CONFIGS: Record<BadgeVariant, BadgeVariantConfig> = {
  new: {
    backgroundColor: semanticColors.recencyBg,
    textColor: '#0C0F24',
    borderColor: semanticColors.recencyBorder,
  },
  'open-house': {
    backgroundColor: semanticColors.success,
    textColor: semanticColors.white,
    icon: SensorDoorIcon,
    ariaLabelSuffix: 'scheduled',
  },
  'virtual-tour': {
    backgroundColor: semanticColors.overlay,
    textColor: semanticColors.white,
    icon: ViewInArIcon,
    ariaLabelSuffix: 'available',
  },
  'price-reduced': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    textColor: '#0C0F24',
    icon: TrendingDownIcon,
  },
  'coming-soon': {
    backgroundColor: semanticColors.white,
    textColor: '#0C0F24',
    icon: CalendarIcon,
  },
  'exp-early-access': {
    backgroundColor: semanticColors.darkNavy,
    textColor: semanticColors.white,
    icon: StarIcon,
  },
  'smart-tag': {
    backgroundColor: semanticColors.white,
    textColor: '#0C0F24',
  },
} as const;

export interface ListingBadgeProps {
  /** The badge variant to render */
  variant: BadgeVariant;
  /** The label text to display */
  label: string;
  /** Optional override for text color (used by price-reduced for theming) */
  textColor?: string;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Config-driven listing badge component
 * 
 * Usage:
 * ```tsx
 * <ListingBadge variant="open-house" label="Open House" />
 * <ListingBadge variant="new" label="New" />
 * <ListingBadge variant="price-reduced" label="Price Reduced" textColor={primaryColor} />
 * ```
 */
export function ListingBadge({ 
  variant, 
  label, 
  textColor: textColorOverride,
  className, 
}: ListingBadgeProps) {
  const config = BADGE_VARIANT_CONFIGS[variant];
  const textColor = textColorOverride ?? config.textColor;
  const IconComponent = config.icon;

  const icon: ReactNode = IconComponent ? (
    <IconWrapper size="sm" color={textColor}>
      <IconComponent />
    </IconWrapper>
  ) : undefined;

  const ariaLabel = config.ariaLabelSuffix 
    ? `${label} ${config.ariaLabelSuffix}` 
    : label;

  return (
    <PillBadge
      size="small"
      backgroundColor={config.backgroundColor}
      textColor={textColor}
      borderColor={config.borderColor}
      icon={icon}
      iconPosition={icon ? 'left' : undefined}
      className={className}
      ariaLabel={ariaLabel}
    >
      {label}
    </PillBadge>
  );
}

/**
 * Get the configuration for a badge variant (for testing/introspection)
 */
export function getBadgeVariantConfig(variant: BadgeVariant): BadgeVariantConfig {
  return BADGE_VARIANT_CONFIGS[variant];
}
